/* diff-render.js — shared unified-diff parser + DOM renderer.
 *
 * Extracted verbatim from diff-panel.js so the same diff rendering is reused
 * across the diff panel, the agent dock (live agent edits), and the inline
 * sync-panel diffs — one renderer, identical look everywhere.
 *
 *   parseUnifiedDiff(text) -> hunks      (pure; Node-testable)
 *   renderDiffInto(el, diff)             (DOM; color-coded hunks)
 */

import { esc } from "./state.js";

/**
 * Parse unified-diff text into hunks.
 * @param {string} diffText
 * @returns {Array<{header:string, lines:Array<{type:'addition'|'removal'|'context', content:string}>}>}
 */
export function parseUnifiedDiff(diffText) {
  if (!diffText) return [];
  const lines = diffText.split("\n");
  const hunks = [];
  let current = null;

  for (const line of lines) {
    if (line.startsWith("@@")) {
      current = { header: line, lines: [] };
      hunks.push(current);
    } else if (current) {
      if (line.startsWith("+")) current.lines.push({ type: "addition", content: line.substring(1) });
      else if (line.startsWith("-")) current.lines.push({ type: "removal", content: line.substring(1) });
      else if (line.startsWith(" ")) current.lines.push({ type: "context", content: line.substring(1) });
      else if (line === "") current.lines.push({ type: "context", content: "" });
    }
  }
  return hunks;
}

/**
 * Render a sync-diff response into a DOM container (color-coded hunks). When
 * there is no textual diff, shows an informational message based on direction.
 * @param {HTMLElement} body
 * @param {{diff?:string, direction?:string}} diff
 */
export function renderDiffInto(body, diff) {
  if (!diff.diff) {
    let info = "";
    if (diff.direction === "local_only") info = "This file exists locally but not on SAP CPQ cloud.";
    else if (diff.direction === "remote_only") info = "This file exists on SAP CPQ cloud but not locally.";
    else info = "No differences found.";
    body.innerHTML = `<div class="diff-empty">${info}</div>`;
    return;
  }

  const hunks = parseUnifiedDiff(diff.diff);
  body.innerHTML = "";
  const container = document.createDocumentFragment();

  for (const hunk of hunks) {
    const hdr = document.createElement("div");
    hdr.className = "diff-hunk-header";
    hdr.textContent = hunk.header;
    container.appendChild(hdr);

    for (const line of hunk.lines) {
      const div = document.createElement("div");
      div.className = `diff-line ${line.type}`;
      const marker = line.type === "addition" ? "+" : line.type === "removal" ? "-" : " ";
      div.innerHTML = `<span class="diff-line-marker">${marker}</span><span class="diff-line-content">${esc(line.content)}</span>`;
      container.appendChild(div);
    }
  }

  body.appendChild(container);
}
